import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { X } from "lucide-react";
import { dapi as api } from "./deskApi";

const COLORS = {
  yellow: { bg: "#fef9c3", bar: "#fde047" },
  orange: { bg: "#ffedd5", bar: "#fdba74" },
  red: { bg: "#fee2e2", bar: "#fca5a5" },
  pink: { bg: "#fce7f3", bar: "#f9a8d4" },
  purple: { bg: "#f3e8ff", bar: "#d8b4fe" },
  blue: { bg: "#e0f2fe", bar: "#7dd3fc" },
  green: { bg: "#dcfce7", bar: "#86efac" },
  gray: { bg: "#f1f5f9", bar: "#cbd5e1" },
};

/** Draggable sticky notes pinned straight onto the desktop surface. */
const StickyNotes = forwardRef(function StickyNotes(_, ref) {
  const [stickies, setStickies] = useState([]);
  const dragRef = useRef(null);
  const timers = useRef({});

  const load = useCallback(() => {
    api.get("/desktop/stickies").then((r) => setStickies(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  useImperativeHandle(ref, () => ({
    addAt: async (x, y) => {
      try {
        const { data } = await api.post("/desktop/stickies", { x, y });
        setStickies((p) => [...p, data]);
      } catch { /* toast handled globally elsewhere */ }
    },
  }));

  const patchNow = (id, sets) => {
    setStickies((p) => p.map((s) => (s.id === id ? { ...s, ...sets } : s)));
    api.patch(`/desktop/stickies/${id}`, sets).catch(() => {});
  };
  const patchText = (id, text) => {
    setStickies((p) => p.map((s) => (s.id === id ? { ...s, text } : s)));
    clearTimeout(timers.current[id]);
    timers.current[id] = setTimeout(() => api.patch(`/desktop/stickies/${id}`, { text }).catch(() => {}), 600);
  };
  const remove = (id) => {
    setStickies((p) => p.filter((s) => s.id !== id));
    api.delete(`/desktop/stickies/${id}`).catch(() => {});
  };

  const startDrag = (e, s) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    dragRef.current = { id: s.id, sx: e.clientX, sy: e.clientY, ox: s.x, oy: s.y };
    const move = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      setStickies((p) => p.map((x) => (x.id === d.id
        ? { ...x, x: Math.max(0, d.ox + ev.clientX - d.sx), y: Math.max(0, d.oy + ev.clientY - d.sy) }
        : x)));
    };
    const up = (ev) => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      const d = dragRef.current;
      dragRef.current = null;
      if (!d) return;
      api.patch(`/desktop/stickies/${d.id}`, {
        x: Math.max(0, d.ox + ev.clientX - d.sx),
        y: Math.max(0, Math.min(window.innerHeight - 120, d.oy + ev.clientY - d.sy)),
      }).catch(() => {});
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  return (
    <>
      {stickies.map((s) => {
        const c = COLORS[s.color] || COLORS.yellow;
        return (
          <div
            key={s.id}
            className="absolute z-[3] w-52 rounded-sm shadow-lg flex flex-col"
            style={{ left: s.x, top: s.y, background: c.bg }}
            onClick={(e) => e.stopPropagation()}
            onContextMenu={(e) => e.stopPropagation()}
            data-testid={`sticky-${s.id}`}
          >
            <div
              className="flex items-center gap-1 px-1.5 py-1 cursor-move rounded-t-sm"
              style={{ background: c.bar, touchAction: "none" }}
              onPointerDown={(e) => startDrag(e, s)}
              title="Drag to move"
            >
              <div className="flex gap-1 flex-1">
                {Object.entries(COLORS).map(([k, v]) => (
                  <button key={k} type="button"
                    className={`w-3 h-3 rounded-full border ${s.color === k ? "border-slate-600" : "border-black/10"}`}
                    style={{ background: v.bar }}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => patchNow(s.id, { color: k })}
                    title={k}
                    data-testid={`sticky-color-${k}-${s.id}`}
                  />
                ))}
              </div>
              <button type="button" className="p-0.5 rounded hover:bg-black/10"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => remove(s.id)} title="Delete sticky note" data-testid={`sticky-delete-${s.id}`}>
                <X className="w-3.5 h-3.5 text-slate-700" />
              </button>
            </div>
            <textarea
              value={s.text || ""}
              onChange={(e) => patchText(s.id, e.target.value)}
              placeholder="Write something…"
              className="w-full h-32 resize-none bg-transparent p-2 text-[13px] leading-snug outline-none text-slate-800 placeholder:text-slate-500/50"
              style={{ fontFamily: "'Segoe Print','Comic Sans MS',cursive" }}
              data-testid={`sticky-text-${s.id}`}
            />
          </div>
        );
      })}
    </>
  );
});

export default StickyNotes;
